import type { DepartureDto, PackageDto } from "@tripcart/shared";

/** Resolve the operator's active package for the booking widget: the
 *  explicitly-selected one, else the default-flagged one, else the first.
 *  Returns null when the experience has no packages. */
export function resolveActivePackage(
  packages?: PackageDto[],
  selectedId?: string,
): PackageDto | null {
  if (!packages?.length) return null;
  if (selectedId)
    return (
      packages.find((p) => p.id === selectedId) ??
      packages.find((p) => p.isDefault) ??
      packages[0]!
    );
  return packages.find((p) => p.isDefault) ?? packages[0]!;
}

export interface BookingConfigState {
  /** The active package has no traveler categories — nothing to price.
   *
   *  Deliberately NOT "no category costs more than zero". That reading briefly
   *  lived here to mirror a server guard on unpriced tours, and it hid the
   *  booking widget behind "not configured" for every deliberately FREE tour —
   *  a supported product, and one the storefront is otherwise happy to sell
   *  ($0 skips payment and auto-confirms). A zero price is a price; the two
   *  cases are not distinguishable from the package alone, and the server no
   *  longer blocks on the total either. */
  hasNoPricing: boolean;
  /** Availability resolved to an empty window (a non-null empty array — not
   *  the still-loading `null`), so there are no dates to book. */
  hasNoAvailability: boolean;
  /** The operator hasn't finished setup: no pricing or no availability.
   *  On-request experiences are excluded — they book via the quote flow,
   *  not the date/price widget, so an empty availability window is expected
   *  and NOT a "not configured" state. */
  isNotConfigured: boolean;
}

/** The single source of truth for the storefront "not configured yet" state,
 *  shared by the booking sidebar (which swaps in `NotConfiguredCard`) and the
 *  mobile sticky bar (which must not invite "Choose date" when there's nothing
 *  to book). Season overrides never add or remove categories, so resolving
 *  from the raw active package matches the sidebar's season-applied count. */
export function resolveBookingConfigState({
  packages,
  selectedPackageId,
  pricingMode,
  departures,
  hasMoreAfter = false,
}: {
  packages?: PackageDto[];
  selectedPackageId?: string;
  pricingMode?: string;
  departures: DepartureDto[] | null;
  /** True when the API's run periods have departures beyond the loaded
   *  window (e.g. a tour scheduled a year out with nothing in the next 90
   *  days). An empty `departures` slice with `hasMoreAfter: true` is a
   *  paging gap, not "operator hasn't scheduled dates" — must not trip the
   *  not-configured state. */
  hasMoreAfter?: boolean;
}): BookingConfigState {
  const activePkg = resolveActivePackage(packages, selectedPackageId);
  const hasNoPricing = (activePkg?.categories.length ?? 0) === 0;
  const hasNoAvailability =
    departures !== null && departures.length === 0 && !hasMoreAfter;
  const isNotConfigured =
    pricingMode !== "onrequest" && (hasNoPricing || hasNoAvailability);
  return { hasNoPricing, hasNoAvailability, isNotConfigured };
}
